import { useCallback, useEffect, useMemo, useState } from 'react';
import { forexApi, type FollowPerformanceSummaryDTO } from '@/services/forexApi';

export function useFollowPerformanceSummary(accessToken: string | null, userId: string | undefined, days = 180) {
  const scope = useMemo(() => ({ accessToken, userId, days }), [accessToken, userId, days]);
  const [snapshot, setSnapshot] = useState<{
    scope: typeof scope;
    summary: FollowPerformanceSummaryDTO;
    readAt: number;
  } | null>(null);
  const [request, setRequest] = useState<{ scope: typeof scope; loading: boolean; error: boolean } | null>(null);
  const [attempt, setAttempt] = useState(0);
  const refresh = useCallback(() => setAttempt(value => value + 1), []);

  useEffect(() => {
    if (!accessToken || !userId) return;
    let active = true;
    setRequest({ scope, loading: true, error: false });
    void forexApi.getFollowPerformanceSummary(accessToken, days).then(summary => {
      if (!active) return;
      // This is the successful API read time, never a Broker/quote timestamp.
      setSnapshot({ scope, summary, readAt: Date.now() });
      setRequest({ scope, loading: false, error: false });
    }).catch(() => {
      if (active) setRequest({ scope, loading: false, error: true });
    });
    return () => { active = false; };
  }, [accessToken, userId, days, scope, attempt]);

  // Scope changes hide the previous user's/period's curve before effects run.
  const current = snapshot?.scope === scope ? snapshot : null;
  const status = request?.scope === scope ? request : null;
  return {
    summary: current?.summary ?? null,
    readAt: current?.readAt ?? null,
    loading: !!accessToken && !!userId && (status?.loading ?? true),
    error: status?.error ?? false,
    refresh,
  };
}
